<main>
  <loading-overlay [isLoading]="isLoading() > 0" />
  <p-toast position="top-center" />

  <div class="content">
    <div class="page__title">
      <div class="page__title_container">
        <h1>{{ 'ApAging_Title' | translateWF: 'AP Aging' }}</h1>
        <div class="page__title_buttons">
          <p-multiSelect
            [options]="providerOptions()"
            [ngModel]="selectedProviders()"
            (ngModelChange)="onProvidersChange($event)"
            optionLabel="label"
            optionValue="value"
            [filter]="true"
            filterBy="label"
            display="chip"
            appendTo="body"
            styleClass="provider-filter"
            [maxSelectedLabels]="2"
            [placeholder]="'ApAging_AllProviders' | translateWF: 'All providers'"
            [selectedItemsLabel]="'ApAging_NProviders' | translateWF: '{0} providers'"
          />
          <p-datepicker
            [ngModel]="anchorDate()"
            (ngModelChange)="onAnchorChange($event)"
            [disabledDays]="nonWednesdays"
            dateFormat="dd/mm/yy"
            appendTo="body"
            styleClass="w-40"
            [pTooltip]="'ApAging_AnchorTooltip' | translateWF: 'Weeks open on a Wednesday'"
          />
          <!-- autoDisplayFirst OFF: with it on, PrimeNG's writeValue echoes the value straight back out
               through ngModelChange the first time it runs, because its internal modelValue is still null at
               that point. That echo re-entered onWeeksChange on page open and fired the report a second
               time. We always supply a value here, so there is nothing for it to auto-display. -->
          <p-select
            [options]="weekOptions"
            [ngModel]="weeks()"
            (ngModelChange)="onWeeksChange($event)"
            [autoDisplayFirst]="false"
            optionLabel="label"
            optionValue="value"
            appendTo="body"
            styleClass="w-24"
            [pTooltip]="'ApAging_WeeksTooltip' | translateWF: 'How many weeks to project'"
          />
          <!-- A menu, not a single button: neither view is the obvious default, so both are an explicit pick. -->
          <p-button
            [label]="'Export' | translateWF: 'Export'"
            icon="pi pi-download"
            variant="outlined"
            [loading]="isExporting()"
            (click)="exportMenu.toggle($event)"
            aria-haspopup="true"
            aria-controls="ap-aging-export-menu"
          />
          <p-menu #exportMenu id="ap-aging-export-menu" [model]="exportMenuItems()" [popup]="true" appendTo="body" />
        </div>
      </div>
    </div>

    @if (report(); as r) {
      <div class="card table-card">
        <p-table [value]="groups()" dataKey="providerReference" [rowHover]="true" [scrollable]="true" scrollHeight="flex" styleClass="p-datatable-sm">
          <ng-template pTemplate="header">
            <tr>
              <th pFrozenColumn class="provider-col sortable" [attr.aria-sort]="ariaSort('provider')" (click)="onSort('provider')">
                <span class="th-label">
                  {{ 'ApAging_Provider' | translateWF: 'Provider' }}
                  <i class="pi sort-icon" [ngClass]="sortIcon('provider')" [class.active]="isSorted('provider')"></i>
                </span>
                @if (isFiltered()) {
                  <p-button
                    [label]="'ApAging_ClearFilter' | translateWF: 'Clear filter'"
                    icon="pi pi-times"
                    variant="text"
                    size="small"
                    (click)="$event.stopPropagation(); clearProviders()"
                  />
                }
              </th>
              @for (col of columns(); track col.index) {
                <th class="text-right num-col sortable" [class.overdue-col]="isOverdueColumn(col)" [attr.aria-sort]="ariaSort(bucketField(col))" (click)="onSort(bucketField(col))">
                  <span class="th-label">
                    {{ columnLabel(col) }}
                    <i class="pi sort-icon" [ngClass]="sortIcon(bucketField(col))" [class.active]="isSorted(bucketField(col))"></i>
                  </span>
                </th>
              }
              <th pFrozenColumn alignFrozen="right" class="text-right num-col total-col sortable" [attr.aria-sort]="ariaSort('total')" (click)="onSort('total')">
                <span class="th-label">
                  {{ 'ApAging_Amount' | translateWF: 'Amount' }}
                  <i class="pi sort-icon" [ngClass]="sortIcon('total')" [class.active]="isSorted('total')"></i>
                </span>
              </th>
            </tr>
          </ng-template>

          <ng-template pTemplate="body" let-group>
            <tr class="provider-row cursor-pointer" (click)="toggleProvider(group)">
              <td pFrozenColumn class="provider-col">
                <div class="provider-line">
                  <i class="pi" [ngClass]="isExpanded(group) ? 'pi-chevron-down' : 'pi-chevron-right'"></i>
                  <div>
                    <div class="provider-name">{{ providerLabel(group) }}</div>
                    <div class="provider-meta">
                      @if (group.providerCode) {
                        {{ group.providerCode }} ·
                      }
                      {{ group.documentCount }} {{ 'ApAging_Documents' | translateWF: 'documents' }}
                      @if (group.blockedDocumentCount > 0) {
                        · <span class="blocked-meta">{{ group.blockedDocumentCount }} {{ 'ApAging_BlockedShort' | translateWF: 'blocked' }}</span>
                      }
                    </div>
                  </div>
                </div>
              </td>
              @for (col of columns(); track col.index) {
                <td class="text-right num-col tabular-nums" [class.overdue-col]="isOverdueColumn(col)">
                  @if (groupCell(group, col.index); as cell) {
                    @if (cell.amount !== 0) {
                      {{ reportingSymbol() }}&nbsp;{{ cell.amount | number: '1.2-2' }}
                    }
                  }
                </td>
              }
              <td pFrozenColumn alignFrozen="right" class="text-right num-col tabular-nums total-col">{{ reportingSymbol() }}&nbsp;{{ group.subtotalAmount | number: '1.2-2' }}</td>
            </tr>

            <!-- Document detail rendered as rows of THIS table, not a nested one, so every amount stays
                 under the week column it belongs to. -->
            @if (isExpanded(group)) {
              <!-- Skeleton rows rather than a spinner: they occupy the same shape the documents will, so
                   the grid does not jump when the response lands. The bucket cells stay empty on purpose —
                   a document fills exactly one of them and we do not yet know which. -->
              @if (isLoadingDocuments(group)) {
                @for (row of skeletonRows(group); track row; let isFirst = $first) {
                  <tr class="doc-row" [attr.aria-hidden]="isFirst ? null : 'true'">
                    <td pFrozenColumn class="provider-col">
                      @if (isFirst) {
                        <span class="sr-only" role="status">{{ 'ApAging_LoadingDocuments' | translateWF: 'Loading documents…' }}</span>
                      }
                      <p-skeleton width="9rem" height="0.9rem" />
                      <p-skeleton width="14rem" height="0.7rem" styleClass="mt-2" />
                    </td>
                    @for (col of columns(); track col.index) {
                      <td class="num-col" [class.overdue-col]="isOverdueColumn(col)"></td>
                    }
                    <td pFrozenColumn alignFrozen="right" class="num-col total-col">
                      <p-skeleton width="4.5rem" height="0.9rem" styleClass="ml-auto" />
                    </td>
                  </tr>
                }
              }
              @for (doc of documentsFor(group); track doc.apDocumentReference) {
                <tr class="doc-row">
                  <td pFrozenColumn class="provider-col">
                    <div class="doc-number">
                      {{ doc.number }}
                      @if (doc.isBlocked) {
                        <p-tag severity="warn" [value]="'ApAging_BlockedTag' | translateWF: 'No certificate'" [pTooltip]="blockedTooltip(doc.blockedReason)" />
                      }
                      @if (doc.isStandaloneNote) {
                        <p-tag severity="info" [value]="'ApAging_Credit' | translateWF: 'Note'" />
                      }
                    </div>
                    <div class="doc-meta">
                      {{ doc.dueDate | date: 'dd/MM/yyyy' }}
                      @if (doc.fiscalNumber) {
                        · {{ doc.fiscalNumber }}
                      }
                      @if (doc.apAccountNumber) {
                        · {{ accountLabel(doc.apAccountName, doc.apAccountNumber) }}
                      }
                      @if (doc.daysPastDue) {
                        · <span class="past-due">{{ doc.daysPastDue }} {{ 'ApAging_DaysPastDue' | translateWF: 'days past due' }}</span>
                      }
                    </div>
                  </td>
                  @for (col of columns(); track col.index) {
                    <td class="text-right num-col tabular-nums" [class.overdue-col]="isOverdueColumn(col)">
                      @if (doc.bucketIndex === col.index) {
                        {{ reportingSymbol() }}&nbsp;{{ doc.amount | number: '1.2-2' }}
                      }
                    </td>
                  }
                  <td pFrozenColumn alignFrozen="right" class="text-right num-col tabular-nums total-col">
                    {{ reportingSymbol() }}&nbsp;{{ doc.amount | number: '1.2-2' }}
                    <!-- What actually moved, when it was not in the reporting currency. Named by its CODE
                         rather than marked with a symbol: the point of this line is to say which currency,
                         and it used to render the raw enum — an HNL invoice read as "1". -->
                    @if (doc.currency !== r.reportingCurrency) {
                      <div class="source-amount">{{ doc.netPayable | number: '1.2-2' }} {{ currencyCode(doc.currency) }}</div>
                    }
                  </td>
                </tr>
              }
            }
          </ng-template>

          <ng-template pTemplate="footer">
            <tr class="totals-row">
              <td pFrozenColumn class="provider-col">
                <strong>{{ 'ApAging_GrandTotal' | translateWF: 'Grand total' }}</strong>
                @if (isFiltered()) {
                  <span class="filtered-note">{{ 'ApAging_FilteredNote' | translateWF: '(filtered)' }}</span>
                }
              </td>
              @for (col of totals(); track col.index) {
                <td class="text-right num-col tabular-nums" [class.overdue-col]="isOverdueColumn(col)">
                  <strong>{{ reportingSymbol() }}&nbsp;{{ col.amount | number: '1.2-2' }}</strong>
                </td>
              }
              <td pFrozenColumn alignFrozen="right" class="text-right num-col tabular-nums total-col">
                <strong>{{ reportingSymbol() }}&nbsp;{{ grandTotal() | number: '1.2-2' }}</strong>
              </td>
            </tr>
          </ng-template>

          <ng-template pTemplate="emptymessage">
            <tr>
              <td [attr.colspan]="columns().length + 2" class="empty-cell">
                <i class="pi pi-inbox"></i>
                @if (isFiltered()) {
                  <p>{{ 'ApAging_EmptyFiltered' | translateWF: 'No open payables for the selected providers.' }}</p>
                } @else {
                  <p>{{ 'ApAging_Empty' | translateWF: 'No open payables for this company.' }}</p>
                }
              </td>
            </tr>
          </ng-template>
        </p-table>
      </div>
    }
  </div>
</main>
